/**
 * This file exports an instantiated Auth object which provides the rest of the application with methods
 * to sign the user in and out and handle other auth related tasks (e.g. sendPasswordResetEmail).
 *
 * The class is also responsible for populating and updating the `authUser` vuex module with the
 * user roles, email, isLoggedIn, and other properties of the authUser. The apps representation
 * of the state of the authUser exists in the vuex store, which is what components and other
 * interested parties should query to check the current auth status.
 *
 * The auth object must be called with its' `initialize()` method before use, in `main.ts`.
 *
 * TODO: describe what authentication is used for (e.g. route access, and passing credentials to
 * the functions and firestore backends)
 *
 * Behind the scenes the Auth class relies on firebase auth to perform authentication
 * tasks but this is abstracted away from the consumers, so there is no direct dependence
 * on firebase auth in the rest of the app.
 *
 * Authentication basically works like this:
 * -----------------------------------------
 * - When the Auth class' sign in / sign out methods are called, it just delegates the task to
 *  firebase auth SDK. End of job. The firebase auth SDK then sends off a asynchronous call to
 *  the firebase auth backend to perform the operation.
 *
 * - The Auth class has attached itself as a listener to changes in firebase auth state. So, when
 *  the auth backend gets back to the firebase SDK with a result, the Auth class gets notified.
 *  If the sign-in were successful a firebase auth user object will be returned, which the Auth
 *  class then maps to the authUser vuex store, with user roles and all. If the user is not
 *  signed-in, the returned firebase auth user is null, and the Auth class updates the vuex
 *  module accordingly, keeping it in sync with the firebase auth status.
 *
 * User roles:
 * -----------------------------------------
 *
 * The list of user roles applicable for this application is defined in `authRoles.ts`.
 *
 * The firebase auth user object, which we receive upon successful authentication with the backend,
 * contains a JWT (Json Web Token). The user's user roles are stored as simple keys in the 'claims'
 * object of the JWT. Upon receiving a user object we simply parse the 'claims' object, compares the
 * keys with our predefined list of user roles (`authRoles.ts`) and puts any matches in the `roles`
 * array of the authUser vuex module. Note, that it is the functions backend which is responsible
 * for actually setting and clearing these user roles on the jwt. In the frontend our only responsibility
 * is to _read_ them. If we want to change them, we have to send a request (command) to the backend.
 *
 * Stored (locally persisted) auth states - `authIsPending`:
 * ---------------------------------------------------------
 *
 * When the application loads, a locally persisted and still valid firebase auth state may already
 * exist, depending on what 'persistence type' (e.g. 'local', 'session' or 'none') we have set up
 * for the firebase auth SDK. A local, and still valid, auth state could for instance exist if the
 * user signs in, navigates away, to another site, and then navigates back to the application
 * (= the application loads again).
 *
 * Out-of-the-box we have no way of knowing if such a valid auth exists when the application loads:
 * there is no method to directly query the firebase auth SDK about the existence of a local auth
 * state. Furthermore, if a state IS persisted, the firebase auth SDK will need to make a roundtrip
 * to the backend to check if the auth state is still valid. This takes approx two secs. It then
 * sends an 'onAuthChange' event ...so we are only eventually informed about it.
 *
 * In short: because of local auth persistence, when the application loads, it may take several
 * seconds before we actually can know if the user is signed in or not. If not handled, this could
 * lead to confusing behavior in the UI. E.g.: when the application loads, it looks to us like the
 * user is not signed in, so we show him the login page. A couple of seconds later we get notified
 * that the user is already signed in and abruptly redirects him away from the login page.
 * Also, we generally don't want to put the user on hold for several seconds, waiting for the
 * firebase SDK to notify us about the auth status, before we show him the appropriate page and
 * let him interact with the application.
 *
 * To mitigate this, we need to be smarter on whether or not wait for a auth state validation
 * from the firebase SDK. We only want to wait if here is a chance that an auth state exists.
 * For this purpose we expose and set the property `authIsPending` on the vuex authUser. When
 * the application loads - and **IF** there is a chance that firebase auth have persisted a
 * valid auth state - we set this to true (... because we know that a validation is underway
 * which may result in the user being logged in). Consumers, e.g. components, could then watch
 * this property and update themselves accordingly, e.g. put the user on hold, by showing a spinner
 * until it is known if he is logged in or out.
 *
 * We keep track of the possibility that firebase auth could have persisted a valid auth state
 * be setting a flag in local storage when the user successfully logs in, IF the firebase auth
 * persistence type is !== 'none'. We remove the flag, when the user signs out. Local storage
 * survives page reloads. In this way we basically track the auth persistence of firebase auth
 * without being able to query it directly. Also we don't need to put the user on hold every time
 * the application loads, only in the specific cases that a valid auth may exist.
 *
 */

import { AuthenticationError, AuthErrorCode } from './authenticationError'
import Firebase from '../firebase/index'
import 'firebase/auth'
import { vxm, store } from '../state/index'
import { AuthUserState } from '../state/authUser/authUser'
import { filterToAuthUserRoles, AuthUserRolesT } from './authRoles'

/**
 * Define the local auth state persistence strategy, i.e. how to handle
 * auth persistence in the browser (across page reloads, etc.)
 * See https://firebase.google.com/docs/auth/web/auth-state-persistence
 * May be: 'local' | 'session' | 'none'.
 */
// eslint-disable-next-line max-len
const FIREBASE_AUTH_PERSISTENCE_TYPE: firebase.auth.Auth.Persistence = Firebase.auth.Auth.Persistence.SESSION

/**
 * Our representation of a JWT (Json Web Token); in our use
 * case it is only required that the jwt is an object which
 * contains a `claims` property.
 */
interface ObjectWithClaims {
  claims: {
    [key: string]: unknown;
  };
}

/**
 * Internal helper function (used below) that maps the claims property
 * of a JWT (Json Web Token) into an array of user roles (e.g. 'admin',
 * 'therapist', etc.).
 *
 * It extracts the keys of the 'claims' property on the jwt and then
 * filter them down to only include keys that are identical to the auth
 * roles  defined for this application.
 *
 * @param jwt
 */
function mapJWTToAuthUserRoles(jwt: ObjectWithClaims): AuthUserRolesT {
  const keys = Object.keys(jwt.claims)
  const roles = filterToAuthUserRoles(keys)
  return roles
}

/**
 * Internal helper function. Takes a Firebase.User object and returns an
 * AuthUser object (for the vuex authUser module).
 *
 * @param fbUser
 */
async function mapFirebaseAuthUserToAuthUser(fbUser: Firebase.User): Promise<AuthUserState> {
  const jwt = await fbUser.getIdTokenResult() // ...returns a firebase JWT
  const roles = mapJWTToAuthUserRoles(jwt) // extracts 'claims' property and maps to user roles

  return {
    email: fbUser.email,
    displayName: fbUser.displayName,
    id: fbUser.uid,
    roles,
    user: fbUser,
  }
}

/**
 * Maps error codes returned by firebase auths' signInWithEmailAndPassword method
 * to local AuthErrorCodes.
 *
 * https://firebase.google.com/docs/reference/js/firebase.auth.Auth#signinwithemailandpassword
 *
 * Incidentally the firebase error codes are reused as the applications AuthErrorCodes
 * (just because they are so descriptive).
 *
 */
const firebaseErrorCodeToErrorCode: {[key: string]: AuthErrorCode} = {
  'auth/invalid-email': AuthErrorCode['invalid-email'],
  'auth/user-disabled': AuthErrorCode['user-disabled'],
  'auth/user-not-found': AuthErrorCode['user-not-found'],
  'auth/wrong-password': AuthErrorCode['wrong-password'],
  'auth/too-many-requests': AuthErrorCode['too-many-requests'], // <- this is not documented (I think) - but I ran into it!
}

/**
 * Class for authentication the user.
 *
 * The class itself is not exported, only an already instantiated object.
 * Before using the auth object, you need to call its `initialize()` method (this happens in `main.ts`).
 *
 * The current state of the user (e.g. loggedIn, user roles, etc.) are stored in vuex AuthUser module, which
 * is where you should query for it. The auth object is only for performing auth operations, it doesn't
 * communicate state, except for to the vuex module.
 *
 * Contrived usage example:
 *
 ```ts
 // somefile

 import { auth } from './auth'
 import { vxm } from '../state'

 auth.initialize()

 try {
   await auth.signIn('test@test.com', 'myPassword')
 } catch (e) {
    // inspect the error
 }

  console.log(vxm.authUser.isLoggedIn) // = true, if the login was successful
  // Note: the sign-in operation is async, so the console.log should be in
  // a computed property in a component, it might not have resolved in this
  // example.
 ```
 *
 */
class Auth {
  /**
   * Reference to the firebase.auth() service
   * Set in the constructor.
   */
  private firebaseAuthService: firebase.auth.Auth

  /**
   * The local auth state persistence strategy, i.e. how to handle auth
   * persistence in the browser (across page reloads, etc.)
   * See https://firebase.google.com/docs/auth/web/auth-state-persistence
   * May be: 'local' | 'session' | 'none'.
   * Set in the constructor.
   */
  private firebaseAuthPersistenceType: firebase.auth.Auth.Persistence // LOCAL | SESSION | NONE

  /**
   * Flag
   */
  private isInitialized = false

  /**
   * The constructor is not used outside this module!
   *
   * The class gets instantiated and exported the bottom of this file. It
   * is the already instantiated auth object that the rest of the application
   * sees. The auth object must, however, be initialized() before use - see
   * that method.
   *
   * @param firebaseAuthService reference to the firebase.auth() service
   * @param persistenceType 'local', session' or 'none'
   */
  constructor(
    firebaseAuthService: firebase.auth.Auth,
    persistenceType: firebase.auth.Auth.Persistence,
  ) {
    this.firebaseAuthService = firebaseAuthService
    this.firebaseAuthPersistenceType = persistenceType
  }

  /**
   * Initialize the class. This must be done before usage i.e. in `main.ts`.
   *
   * It sets the auth persistence type (i.e. 'session', 'local', 'none') and adds
   * a listener for changes in firebase auth state, so that we get notified
   * of changes and can react accordingly.
   *
   * It also checks if we might already have a valid auth in local persistence.
   */
  public async initialize() {
    if (this.isInitialized) return

    // Initialize, TODO:handle error case on set persistence
    this.firebaseAuthService.setPersistence(this.firebaseAuthPersistenceType)

    // Check if we are in a situation where an auth state may be persisted
    // locally by firebase auth. If so, set authPending to true, because
    // we will need to wait for firebase auth to tell us if the auth is valid.
    if (this.localAuthStateCanBePersisted()) {
      // check if we have a flag that the user was logged in earlier
      if (this.getLoggedInFlag()) {
        this.setAuthIsPending(true)
      }
    }

    // Attach our self (our private method `this.onAuthStateChange()`)
    // as a listener on firebase auth state changes, so that we get notified
    // when the firebase auth state changes.
    this.firebaseAuthService.onAuthStateChanged(async (user) => {
      await this.onAuthStateChange(user)
    })

    this.isInitialized = true
  }

  /**
   * Sign in the user to the backend authentication service with email and password.
   * If sign-in fails an `AuthenticationError` error is thrown, with an error code
   * detailing the cause.
   *
   * @param email
   * @param password
   */
  public async signIn(email: string, password: string) {
    // Always sign out the current user before attempting a new sign in
    await this.signOut()
    this.setAuthIsPending(true)
    try {
      // Dev note: I originally wrapped this in a timeout function. However, if the
      // timeout kicks in, the call to Firebase.Auth will still proceed and log in
      // the user eventually which would lead to strange behavior. I didn't find a
      // workaround and had to abandon the time out functionality for now.
      await this.firebaseAuthService.signInWithEmailAndPassword(email, password)
    } catch (e) {
      this.setAuthIsPending(false)
      const authErr = this.getAuthenticationErrorFromError(e)
      throw authErr
    }
  }

  /**
   * Signs the user out of the application.
   *
   * If it fails an `AuthenticationError` error is thrown with an error code
   * detailing the cause.
   */
  public async signOut() {
    this.setAuthIsPending(true)
    try {
      await this.firebaseAuthService.signOut()
      store.dispatch('clearState')
    } catch (e) {
      this.setAuthIsPending(false)
      const authErr = this.getAuthenticationErrorFromError(e)
      throw authErr
    }
  }

  /**
   * Sends a password reset mail to the specified email address.
   *
   * The email must belong to an existing user. If the operation fails
   * an AuthenticationError is thrown holding an error code detailing the
   * cause (e.g. 'auth/unknown-user').
   *
   * Dev note: if the error is caused by a badly formatted email address
   * (e.g. 'xxxx@xxx') the console will complain about an uncaught error,
   * thrown by firebase auth, though in fact we do catch an instance of
   * that error it in our own code. Maybe the error it is thrown twice by
   * firebase. Can't fix now.
   *
   * @param email
   */
  public async sendPasswordResetEmail(email: string) {
    try {
      await this.firebaseAuthService.sendPasswordResetEmail(email)
    } catch (e) {
      const authErr = this.getAuthenticationErrorFromError(e)
      throw authErr
    }
  }

  /**
   * Private convenience method for handling errors. Takes an error, e.g.
   * a firebase auth error, maps it to an `AuthenticationError` which is
   * then returned. The returned error can then be thrown by the client
   * code.
   *
   * @param e An error like object
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getAuthenticationErrorFromError(e: any): Error {
    if ('code' in e) {
      // If it is a known firebase error code, then map it to local error code:
      if (e.code in firebaseErrorCodeToErrorCode) {
        const localCode = firebaseErrorCodeToErrorCode[e.code]
        return new AuthenticationError(localCode, e.message)
      // else if it is not a firebase error (or an unknown firebase error)
      }
      return new AuthenticationError(AuthErrorCode['unknown-server-error'])
    }
    return new AuthenticationError(AuthErrorCode.other, e.message)
  }

  /**
   * Convenience method - returns true if the auth mechanism allows for persisting
   * auth state between pages loads (i.e. if, when the application loads or reloads,
   * a valid auth state could already be present). This depends on the auth
   * persistence type.
   */
  private localAuthStateCanBePersisted(): boolean {
    return (this.firebaseAuthPersistenceType !== Firebase.auth.Auth.Persistence.NONE)
  }

  /**
   * Our listener method that gets called from firebase auth each time there is a change
   * in firebase auth user state (we set this up in the initialize() method).
   *
   * If the user is logged out, the `user` parameter will be `null`.
   *
   * @param user
   *
   */
  private async onAuthStateChange(user: Firebase.User | null) {
    this.setAuthIsPending(false)
    if (user) {
      if (this.localAuthStateCanBePersisted()) this.setLoggedInFlag(true)
      const authUser = await mapFirebaseAuthUserToAuthUser(user)
      vxm.authUser.setUser(authUser)
    } else {
      this.setLoggedInFlag(false)
      vxm.authUser.clearUser()
    }
  }

  /**
   * Our private method for remembering, across page loads, that the firebase
   * auth persisted a valid auth session.
   *
   * Set to true, when the user logs in, and if firebase auth is configured
   * to persist auth state. Set to false, when the user logs out.
   *
   * The flag is saved in local storage to persist between page loads.
   *
   * @param loggedIn
   *
   */
  private setLoggedInFlag = (loggedIn: boolean) => {
    if (loggedIn) {
      localStorage.setItem('loggedIn', '1')
    } else {
      localStorage.removeItem('loggedIn')
    }
  }

  /**
   * Returns true if firebase auth could have saved a valid auth state.
   * Warning: It doesn't tell us if the user actually is still logged in!
   * (we need to listen for changes in the firebase auth status for that).
  */
  private getLoggedInFlag = () => (localStorage.getItem('loggedIn') !== null)

  /**
   * Notifies the vuex authUser store, that an auth is pending.
   *
   * Components could then listen to this property on the authUser
   * and e.g. show a spinner.
   *
   * @param isPending
   */
  private setAuthIsPending(isPending: boolean) {
    vxm.authUser.setAuthIsPending(isPending)
  }
}

const auth = new Auth(Firebase.auth(), FIREBASE_AUTH_PERSISTENCE_TYPE)
export { auth }
